/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { StudyCohort, LoadStatus } from "../../../types";
import { getFormattedDate } from "../../../utils";

export interface ClassesState {
  classes: Record<string, StudyCohort>;
  classLoadStatus: LoadStatus;
}

const initialState: ClassesState = {
  classes: {},
  classLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadClasses = createAsyncThunk(
  "classes/loadClasses",
  async (_, thunkAPI): Promise<StudyCohort[]> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user) {
      throw new Error("login required");
    }
    return [];
  }
);

export const addOrUpdateClass = createAsyncThunk(
  "classes/addOrUpdateClass",
  async (data: Partial<StudyCohort>, thunkAPI): Promise<StudyCohort> => {
    if (!data.id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    const user = state.login.user;
    if (!user) throw new Error("login required");
    const classes = state.classes.classes;
    const date = getFormattedDate(new Date());
    if (classes[data.id]) {
      return {
        ...classes[data.id],
        ...data,
        updatedAt: date,
      };
    } else {
      return {
        ...(data as StudyCohort),
        createdAt: date,
        updatedAt: date,
      };
    }
  }
);

export const deleteClass = createAsyncThunk(
  "classes/deleteClass",
  async (id: string, thunkAPI): Promise<void> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    const user = state.login.user;
    if (!user) throw new Error("login required");
    if (!state.classes.classes[id]) {
      throw new Error("class does not exist");
    }
  }
);

export const classesSlice = createSlice({
  name: "classes",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load classes
      .addCase(loadClasses.pending, (state) => {
        state.classLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadClasses.fulfilled, (state, action) => {
        for (const c of action.payload) {
          state.classes[c.id] = c;
        }
        state.classLoadStatus = LoadStatus.DONE;
      })
      .addCase(loadClasses.rejected, (state) => {
        state.classLoadStatus = LoadStatus.FAILED;
      })
      // add or update class
      .addCase(addOrUpdateClass.fulfilled, (state, action) => {
        const c = action.payload;
        state.classes[c.id] = c;
      })
      // delete class
      .addCase(deleteClass.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.classes[id];
      });
  },
});

export default classesSlice.reducer;
